import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Eye, EyeOff, ChevronUp, ChevronDown, Check, X, ArrowUp, ArrowDown } from 'lucide-react';
import type { Database } from '@/lib/database.types';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { toast } from 'react-hot-toast';

type Product = Database['public']['Tables']['products']['Row'];
type SortField = 'name' | 'price' | 'stock_level' | 'is_published';
type SortDirection = 'asc' | 'desc';

export function ProductsList() {
  const { user } = useAuth();
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editingStock, setEditingStock] = React.useState<string | null>(null);
  const [newStockValue, setNewStockValue] = React.useState<number>(0);
  const [updatingStock, setUpdatingStock] = React.useState(false);
  const [sortField, setSortField] = React.useState<SortField>('name');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');

  React.useEffect(() => {
    if (!user || !user.id) return;

    async function fetchProducts() {
      const userId = user.id;
      
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('producer_id', userId);

      if (error) {
        console.error('Error fetching products:', error);
        return;
      }

      // Apply sorting in memory for fields that might not be directly sortable in Supabase
      let sortedData = [...(data || [])];
      
      if (sortField === 'name') {
        sortedData.sort((a, b) => {
          return sortDirection === 'asc' 
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        });
      } else if (sortField === 'price') {
        sortedData.sort((a, b) => {
          return sortDirection === 'asc'
            ? a.price - b.price
            : b.price - a.price;
        });
      } else if (sortField === 'stock_level') {
        sortedData.sort((a, b) => {
          return sortDirection === 'asc'
            ? a.stock_level - b.stock_level
            : b.stock_level - a.stock_level;
        });
      } else if (sortField === 'is_published') {
        sortedData.sort((a, b) => {
          if (sortDirection === 'asc') {
            return a.is_published === b.is_published ? 0 : a.is_published ? 1 : -1;
          } else {
            return a.is_published === b.is_published ? 0 : a.is_published ? -1 : 1;
          }
        });
      }

      setProducts(sortedData);
      setLoading(false);
    }

    fetchProducts();
  }, [user, sortField, sortDirection]);

  // Handler for sorting column
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ArrowUp className="ml-1 h-4 w-4 inline" /> : 
      <ArrowDown className="ml-1 h-4 w-4 inline" />;
  };

  async function togglePublished(product: Product) {
    try {
      const { error } = await supabase
        .from('products')
        .update({ is_published: !product.is_published })
        .eq('id', product.id);

      if (error) throw error;

      setProducts(products.map(p =>
        p.id === product.id ? { ...p, is_published: !p.is_published } : p
      ));
    } catch (error) {
      console.error('Error toggling product visibility:', error);
    }
  }

  const startStockEdit = (product: Product) => {
    setEditingStock(product.id);
    setNewStockValue(product.stock_level);
  };

  const cancelStockEdit = () => {
    setEditingStock(null);
  };

  const handleStockUpdate = (change: number) => {
    setNewStockValue(prev => {
      const updated = prev + change;
      return updated >= 0 ? updated : 0;
    });
  };

  const handleStockInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Allow empty string for better UX while typing
    if (inputValue === '') {
      setNewStockValue(0);
    } else {
      const value = parseInt(inputValue);
      if (!isNaN(value)) {
        // Ensure value is not negative
        setNewStockValue(Math.max(0, value));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, productId: string) => {
    if (e.key === 'Enter') {
      saveStockChange(productId);
    } else if (e.key === 'Escape') {
      cancelStockEdit();
    }
  };

  const saveStockChange = async (productId: string) => {
    if (editingStock) {
      setUpdatingStock(true);
      try {
        const { error } = await supabase
          .from('products')
          .update({ 
            stock_level: newStockValue,
            updated_at: new Date().toISOString()
          })
          .eq('id', productId);

        if (error) throw error;

        setProducts(products.map(p =>
          p.id === productId ? { ...p, stock_level: newStockValue } : p
        ));
        
        toast.success('Lagersaldo uppdaterat');
        setEditingStock(null);
      } catch (error) {
        console.error('Error updating stock level:', error);
        toast.error('Kunde inte uppdatera lagersaldo');
      } finally {
        setUpdatingStock(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Produkter</h1>
          <p className="mt-2 text-sm text-gray-700">
            Hantera din produktkatalog
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Link
            to="new"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Lägg till produkt
          </Link>
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {products.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="mt-2 text-sm font-medium text-gray-900">Inga produkter</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Kom igång genom att skapa en ny produkt.
                </p>
                <div className="mt-6">
                  <Link
                    to="new"
                    className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Lägg till produkt
                  </Link>
                </div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th 
                      scope="col" 
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      Produkt {getSortIcon('name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                      onClick={() => handleSort('price')}
                    >
                      Pris {getSortIcon('price')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                      onClick={() => handleSort('stock_level')}
                    >
                      Lager {getSortIcon('stock_level')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                      onClick={() => handleSort('is_published')}
                    >
                      Status {getSortIcon('is_published')}
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-0">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <ImageWithFallback
                              className="h-10 w-10 rounded-full object-cover"
                              src={product.image_urls[0] || ''}
                              alt={product.name}
                              width={40}
                              height={40}
                              quality={60}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="font-medium text-gray-900">{product.name}</div>
                            <div className="text-gray-500">{product.description?.slice(0, 50)}...</div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {product.price.toFixed(2)} Kr
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {editingStock === product.id ? (
                          <div className="flex items-center">
                            <div className="flex items-center rounded-md border border-gray-300 shadow-sm">
                              <button 
                                onClick={() => handleStockUpdate(-1)}
                                disabled={newStockValue <= 0 || updatingStock}
                                className="px-2 py-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 focus:outline-none"
                                aria-label="Minska lager"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>
                              <input
                                type="text"
                                value={newStockValue}
                                onChange={handleStockInputChange}
                                onKeyDown={(e) => handleKeyDown(e, product.id)}
                                disabled={updatingStock}
                                className="w-16 px-2 py-1 text-center border-0 focus:ring-0 focus:outline-none"
                                aria-label="Lagersaldo"
                              />
                              <button 
                                onClick={() => handleStockUpdate(1)}
                                disabled={updatingStock}
                                className="px-2 py-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 focus:outline-none"
                                aria-label="Öka lager"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="flex space-x-1 ml-2">
                              <button 
                                onClick={() => saveStockChange(product.id)}
                                disabled={updatingStock}
                                className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                                aria-label="Spara"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={cancelStockEdit}
                                disabled={updatingStock}
                                className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                aria-label="Avbryt"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button 
                            onClick={() => startStockEdit(product)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
                            title="Klicka för att ändra lagersaldo"
                          >
                            {product.stock_level}
                          </button>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.is_published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {product.is_published ? 'Publicerad' : 'Utkast'}
                        </span>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => togglePublished(product)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            {product.is_published ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                          <Link
                            to={`${product.id}/edit`}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <Edit className="h-5 w-5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}